import { lazy, Suspense, memo, useState, useEffect } from "react";

interface DraggableKaiProps {
  customName?: string;
  showSpeech?: boolean;
}

// Lazy load the heavy DraggableKai component
const DraggableKaiInner = lazy(() => import("./DraggableKai").then(m => ({ default: m.DraggableKai })));

// Simple placeholder while loading
const KaiPlaceholder = () => (
  <div className="fixed bottom-20 right-4 md:bottom-24 md:right-8 z-50 w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-primary to-secondary opacity-50 animate-pulse" />
);

// Wrapper that delays loading until after initial render
export const DraggableKaiLazy = memo(({ customName = "Kai", showSpeech = true }: DraggableKaiProps) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  
  // Check visibility setting
  const [isVisible, setIsVisible] = useState(() => {
    if (typeof window === 'undefined') return true;
    const setting = localStorage.getItem("kai_ai_visible");
    return setting !== "false";
  });

  useEffect(() => {
    // Delay loading by 1 second to prioritize main content
    const timer = setTimeout(() => setShouldLoad(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleStorage = () => {
      const setting = localStorage.getItem("kai_ai_visible");
      setIsVisible(setting !== "false");
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  if (!isVisible) return null;
  if (!shouldLoad) return <KaiPlaceholder />;

  return (
    <Suspense fallback={<KaiPlaceholder />}>
      <DraggableKaiInner customName={customName} showSpeech={showSpeech} />
    </Suspense>
  );
});

DraggableKaiLazy.displayName = "DraggableKaiLazy";
